import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, CheckCircle, XCircle } from 'lucide-react';

const getScoreColor = (s) => (s == null ? '#94a3b8' : s >= 80 ? '#16a34a' : s >= 50 ? '#d97706' : '#dc2626');

/**
 * Usage: <AssessmentReportModal submissionId={selectedSubmissionId} onClose={() => setSelectedSubmissionId(null)} />
 * Renders nothing if submissionId is null/undefined.
 */
export default function AssessmentReportModal({ submissionId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!submissionId) return;
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/assessment/submission/${submissionId}`);
      setData(res.data);
    } catch (err) {
      console.error('Fetch Submission Detail Error:', err);
      setError(err.response?.data?.message || 'Failed to load assessment report');
    } finally {
      setLoading(false);
    }
  };

  if (!submissionId) return null;

  const getAnswerFor = (questionId) => data?.answers?.find((a) => a.questionId === questionId)?.answer || '';
  const getEvalFor = (questionId) => data?.evaluation?.find((e) => e.questionId === questionId);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '720px' }}>
        <div className="modal-header">
          <div>
            <h2>Assessment Report</h2>
            {data && <p>{data.candidate.name} &bull; {data.candidate.email}</p>}
          </div>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-body">
          {loading && <div style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Loading report...</div>}

          {error && <div style={{ padding: '30px', textAlign: 'center', color: '#dc2626' }}>{error}</div>}

          {data && !loading && (
            <>
              {/* OVERALL SCORE */}
              <div className="detail-section">
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: '#f8fafc', padding: '16px', borderRadius: '8px'
                }}>
                  <span style={{ fontWeight: 600, color: '#334155' }}>Overall Percentage</span>
                  <span style={{ fontSize: '1.5rem', fontWeight: 700, color: getScoreColor(data.overallScore) }}>
                    {data.overallScore != null ? `${data.overallScore}/100` : 'Evaluation Pending'}
                  </span>
                </div>
                <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#94a3b8' }}>
                  Submitted: {data.submittedAt ? new Date(data.submittedAt).toLocaleString() : '--'}
                </div>
              </div>

              {/* QUESTION-WISE BREAKDOWN */}
              <div className="detail-section">
                <h4>Question-wise Evaluation</h4>
                {(data.questions || []).map((q, idx) => {
                  const answer = getAnswerFor(q.id);
                  const evalResult = getEvalFor(q.id);
                  return (
                    <div key={q.id} style={{
                      marginBottom: '16px', paddingBottom: '16px',
                      borderBottom: idx < data.questions.length - 1 ? '1px solid #f1f5f9' : 'none'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                        <p style={{ fontWeight: 600, color: '#1e293b', margin: 0 }}>
                          {idx + 1}. {q.question}
                          <span style={{
                            marginLeft: '8px', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase',
                            color: '#64748b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px'
                          }}>
                            {q.type === 'mcq' ? 'MCQ' : q.type === 'coding' ? 'Coding' : 'Short Answer'}
                          </span>
                        </p>
                        {evalResult && (
                          <span style={{
                            flexShrink: 0, fontWeight: 700, fontSize: '0.9rem',
                            color: getScoreColor(evalResult.score * 10)
                          }}>
                            {evalResult.score}/10
                          </span>
                        )}
                      </div>

                      <div style={{
                        marginTop: '8px', padding: '10px', background: '#f8fafc', borderRadius: '6px',
                        fontSize: '0.85rem', color: '#334155',
                        fontFamily: q.type === 'coding' ? 'monospace' : 'inherit', whiteSpace: 'pre-wrap'
                      }}>
                        {answer || <span style={{ color: '#cbd5e1' }}>No answer submitted</span>}
                      </div>

                      {q.type === 'mcq' && q.correctAnswer && (
                        <div style={{ marginTop: '6px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {answer === q.correctAnswer ? (
                            <><CheckCircle size={14} color="#16a34a" /><span style={{ color: '#16a34a' }}>Correct</span></>
                          ) : (
                            <><XCircle size={14} color="#dc2626" /><span style={{ color: '#dc2626' }}>Correct answer: {q.correctAnswer}</span></>
                          )}
                        </div>
                      )}

                      {evalResult?.feedback && (
                        <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#64748b', fontStyle: 'italic' }}>
                          AI feedback: {evalResult.feedback}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}